import axios from "axios";
import { omitBy, isEmpty, isNil } from "lodash";
import { getAccessToken } from "Helpers/session";
import {
  AccountRole,
  DefaultFilter,
  Pagination,
  PhoneInputType,
  Sorting,
  Statuses,
} from "Types/common.types";
import errorHandler from "Utils/error-handler";
import { UserCreationInput, UserRoleToAssign } from "Types/user.types";
import normalizer from "Utils/normalizer";
import { orgName } from "Components/shared/helpers/validations";
import useSession from "Hooks/session.hook";

export interface UserListingFilters extends DefaultFilter {
  orFields?: string;
  status?: "ACTIVE" | Statuses.DEACTIVATE | "PENDING_ACCOUNT";
  roleSubType?: UserRoleToAssign;
  roleType?: AccountRole.COMPANY | AccountRole.CUSTOMER;
}

const url = `${process.env.API_URL}/${process.env.API_VER}`;
const { org, roles } = useSession();

export const fetchUserList = async (
  adminOrgType: AccountRole,
  adminOrgID: string,
  orgID: string[],
  orgType: AccountRole,
  filter: UserListingFilters,
  pagination?: Pagination,
  sorting?: Sorting
) => {
  const accessToken = getAccessToken();
  // console.log('in the services',
  // '\n adminOrgType:', adminOrgType,
  // '\n adminOrgID: ', adminOrgID,
  // '\n orgID: ', orgID,
  // '\n orgType: ', orgType,
  // '\n filter: ', filter,
  // '\n pagination: ', pagination,
  // '\n sorting: ', sorting
  // )
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/employee/ByOrgHeirachy/list`,
      data: omitBy(
        {
          adminOrgType,
          adminOrgID,
          orgID,
          orgType,
          filter,
          pagination,
          sorting,
        },
        (payload) => isNil(payload) || isEmpty(payload)
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.map((respond) => {
      return normalizer.response.user_profile(respond);
    });
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchUserList(adminOrgType, adminOrgID, orgID, orgType, filter);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      return { error: { message: err.message } };
    }
  }
};

export const fetchSingleUser = async (
  viewerProfileID: string,
  viewerOrgID: string,
  targetProfileID: string,
  targetOrgID: string
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/employee/profile/fetch`,
      data: {
        viewerProfileID,
        viewerOrgID,
        targetProfileID,
        targetOrgID,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!isEmpty(response.data)) return normalizer.response.user_profile(response.data);
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await fetchSingleUser(viewerProfileID, viewerOrgID, targetProfileID, targetOrgID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      return { error: { message: err.message } };
    }
  }
};

export const createUser = async (data: UserCreationInput) => {
  const accessToken = getAccessToken();
  const endpoint = {
    company: "/employee/company_employee/register",
    customer: "/employee/customer_employee/register",
  };

  try {
    const response = await axios({
      method: "POST",
      url: `${url}${endpoint[org.orgType.toLowerCase()]}`,
      data: normalizer.model.user_create(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!isEmpty(response.data)) return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createUser(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      return { error: { message: err.message } };
    }
  }
};

export const updateUser = async (data: UserCreationInput) => {
  const accessToken = getAccessToken();
  const endpoint = {
    company: "/employee/company_employee/update",
    customer: "/employee/customer_employee/update",
  };
  try {
    const response = await axios({
      method: "POST",
      url: `${url}${endpoint[org.orgType.toLowerCase()]}`,
      data: normalizer.model.user_create(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!isEmpty(response.data)) return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateUser(data);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      return { error: { message: err.message } };
    }
  }
};

export const userActivation = async (
  adminProfileID: string,
  employeeProfileID: string,
  action: "ACTIVE" | "DEACTIVATED",
  reason?: string
) => {
  const accessToken = getAccessToken();
  const endpoint = {
    company: "/employee/company_employee/status/update",
    customer: "/employee/customer_employee/status/update",
  };
  try {
    const response = await axios({
      method: "POST",
      url: `${url}${endpoint[org.orgType.toLowerCase()]}`,
      data: {
        adminProfileID: adminProfileID,
        employeeProfileID: employeeProfileID,
        status: action,
        reason: reason,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!isEmpty(response.data)) return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await userActivation(adminProfileID, employeeProfileID, action, reason);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      return { error: { message: err.message } };
    }
  }
};

export const fetchUserStatistics = async (companiesIDs?: string[]) => {
  try {
    const accessToken = getAccessToken();
    const endpoint = "/employee/EmployeeCountByOrgHeirachy/fetch";
    const response = await axios({
      method: "POST",
      url: `${url}${endpoint}`,
      data: omitBy(
        {
          adminOrgType: org.orgType,
          adminOrgID: org._id,
          orgType: roles[0].type,
          orgID: companiesIDs,
        },
        isEmpty
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!isEmpty(response.data)) return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchUserStatistics(companiesIDs);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      return { error: { message: err.message } };
    }
  }
};
